'use client';

import { useLoginWithEmail } from '@privy-io/react-auth';
import {
	ConnectedStandardSolanaWallet,
	useCreateWallet,
	useWallets,
} from '@privy-io/react-auth/solana';
import { useCallback, useState } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

export default function usePrivyEmailAuth() {
	const { sendCode, loginWithCode } = useLoginWithEmail();
	const { createWallet } = useCreateWallet();
	const { wallets } = useWallets();

	const [code, setCode] = useState('');

	const createWalletRef = useSyncedRef(createWallet);
	const walletsRef = useSyncedRef(wallets);

	const getEmbeddedWallet = useCallback(
		(preferredAddress?: string): ConnectedStandardSolanaWallet | undefined => {
			const possibleEmbeddedWallets = walletsRef.current.filter(
				(wallet) => wallet.standardWallet.name === 'Privy'
			);

			if (preferredAddress) {
				const preferredWallet = possibleEmbeddedWallets.find(
					(wallet) => wallet.address === preferredAddress
				);
				if (preferredWallet) {
					return preferredWallet;
				}
			}

			return possibleEmbeddedWallets[0];
		},
		[]
	);

	const waitForEmbeddedWallet = useCallback(
		async (preferredAddress?: string) => {
			const embeddedWallet = getEmbeddedWallet(preferredAddress);
			if (embeddedWallet) {
				return embeddedWallet;
			}

			for (let attempt = 0; attempt < 20; attempt += 1) {
				await new Promise((resolve) => setTimeout(resolve, 150));
				const syncedEmbeddedWallet = getEmbeddedWallet(preferredAddress);
				if (syncedEmbeddedWallet) {
					return syncedEmbeddedWallet;
				}
			}

			return undefined;
		},
		[getEmbeddedWallet]
	);

	const ensureEmbeddedWallet = useCallback(async () => {
		let createdWalletAddress: string | undefined;

		try {
			const createdWalletResult = await createWalletRef.current({
				createAdditional: false,
			});
			createdWalletAddress = createdWalletResult.wallet.address;
		} catch (error: unknown) {
			if (
				!(error as Error).message.includes(
					'User already has an embedded wallet.'
				)
			) {
				throw error;
			}
		}

		const embeddedWallet = await waitForEmbeddedWallet(createdWalletAddress);

		if (!embeddedWallet) {
			throw new Error('No Privy embedded wallet found');
		}

		return embeddedWallet;
	}, [waitForEmbeddedWallet]);

	const verifyCodeAndEnsureEmbeddedWallet = useCallback(async () => {
		await loginWithCode({ code });

		return ensureEmbeddedWallet();
	}, [code, loginWithCode, ensureEmbeddedWallet]);

	return {
		code,
		setCode,
		sendCode,
		verifyCodeAndEnsureEmbeddedWallet,
	};
}
